"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import Layout from "../components/layouts/layout.comp";
import { ListSection } from "../components/reuseables/list.comp";
import { deleteCategories, getCategories, groupByParent } from "../core/category.core";
import { useTranslation } from "../core/translation.core";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import { Category } from "../types/category";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { SelectFormData } from "../types/form";
import CategoryRows from "../components/category/categoryRows.comp";
import { BatchAction, BatchToggleButton } from "../components/reuseables/batch.comp";
import AddLink from "../components/form/addLink.comp";

export default function CategoryPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.category.category)} titleBtn={<AddLink id="category-create" href="/category/create" />}>
      <Main />
    </Layout>
  );
}

function Main() {
  const methods = useForm<SelectFormData>({
    defaultValues: {
      option: "",
      selectIds: [],
    },
  });
  useSyncParent(methods);

  return (
    <>
      <OperationPanel methods={methods} />
      <ListSection>
        <CategoriesTable methods={methods} />
      </ListSection>
    </>
  );
}

function useSyncParent(methods: UseFormReturn<SelectFormData, any>) {
  const selectIds = useWatch({ name: "selectIds", control: methods.control });
  const {
    data: categories,
    isError,
    failureReason,
    isFetched,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  function selectChildren(selectIds: string[], selectId: string, categories: Category[]) {
    let hasChange = false;

    const childIds = categories!.filter((c) => c.parentId === selectId);

    for (const childId of childIds) {
      if (!selectIds.includes(childId.id)) {
        selectIds.push(childId.id);
        hasChange = true;
      }

      if (selectChildren(selectIds, childId.id, categories)) {
        hasChange = true;
      }
    }

    return hasChange;
  }

  useEffect(() => {
    function sync(selectIds: string[]) {
      if (!categories) return;

      let hasChange = false;

      // check all children
      for (const selectId of selectIds) {
        if (selectChildren(selectIds, selectId, categories)) {
          hasChange = true;
        }
      }

      if (hasChange) methods.setValue("selectIds", selectIds);
    }

    sync(selectIds);
    // eslint-disable-next-line
  }, [selectIds, categories]);
}

const CategoriesTable = ({ methods }: { methods: UseFormReturn<SelectFormData, any> }) => {
  const { t, msg } = useTranslation();

  return (
    <div id="category-section" className="pt-6 lg:pt-0 dark:text-white">
      <div className="hidden lg:grid lg:grid-cols-[15px_4fr_4fr_4fr_60px_60px_60px_110px_110px_60px] gap-6 pr-6 py-3 text-sm font-medium">
        <div></div>
        <div>{t(msg.category.code)}</div>
        <div>{t(msg.category.name)}</div>
        <div>{t(msg.category.slug)}</div>
        <div>{t(msg.category.rewardPayout)}</div>
        <div>{t(msg.category.isPromotion)}</div>
        <div>{t(msg.general.enable)}</div>
        <div>{t(msg.product.featuredImage)}</div>
        <div>{t(msg.category.icon)}</div>
        <div></div>
      </div>
      <CategoryTableRows methods={methods} />
    </div>
  );
};

function CategoryTableRows({ methods }: { methods: UseFormReturn<SelectFormData, any> }) {
  const [draggingCat, setDraggingCat] = useState<Category>();
  const [dragToCat, setDragToCat] = useState<Category>();

  const {
    data: categories,
    isError,
    failureReason,
    isFetched,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched) return <LoadingOvalCenterIcon />;

  if (!categories) return null;

  const groupedCats = groupByParent(categories);

  return (
    <CategoryRows
      groupedCats={groupedCats}
      draggingCat={draggingCat}
      setDraggingCat={setDraggingCat}
      dragToCat={dragToCat}
      setDragToCat={setDragToCat}
      methods={methods}
    />
  );
}

function OperationPanel({ methods }: { methods: UseFormReturn<SelectFormData, any> }) {
  const queryClient = useQueryClient();

  async function onSubmit(data: SelectFormData) {
    switch (data.option) {
      case "delete":
        await deleteCategories(data.selectIds, queryClient);
        methods.setValue("selectIds", []);
      default:
        return;
    }
  }

  return (
    <div className="flex gap-4 items-center px-4 bg-white dark:bg-her-darkSectionBg shadow-sm py-2 mb-6 rounded-2xl dark:text-slate-300">
      <OperationLabelRow methods={methods} />
      <BatchToggleButton methods={methods} />
      <BatchAction methods={methods} onSubmit={onSubmit} />
    </div>
  );
}

function OperationLabelRow({ methods }: { methods: UseFormReturn<SelectFormData, any> }) {
  const { t, msg } = useTranslation();
  const selectedIds = useWatch({ name: "selectIds", control: methods.control });
  const enableSelect = selectedIds && selectedIds.length > 0;

  if (enableSelect) return null;

  return <div className="h-9 my-2 flex items-center">{t(msg.form.selectForBatchActions)}</div>;
}
